import PublicLayout from "@/components/PublicLayout";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, MailWarning } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState("");
  // Simple client-side sanity check on the token shape (JWT has 3 dot-separated parts).
  const looksLikeJwt = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token);

  useEffect(() => {
    if (token && !looksLikeJwt) {
      setTokenError("This reset link looks malformed. Please request a new email.");
    }
  }, [token, looksLikeJwt]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    if (!token) return toast.error("Missing reset token — open the link from your email");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset. You can sign in now.");
      setTimeout(() => nav("/login", { replace: true }), 1600);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Reset failed. Request a new link.";
      setTokenError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section className="py-16 gradient-radial-blue">
        <div className="max-w-md mx-auto px-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="reset-card">
            <div className="section-label">Reset Password</div>
            <h1 className="mt-2 font-display font-bold text-3xl text-[#0A192F]">Set a new password</h1>
            <p className="text-sm text-slate-600 mt-1">Choose a strong password. Your reset link is valid for 30 minutes.</p>

            {done ? (
              <div className="mt-6 p-4 rounded-md bg-emerald-50 border border-emerald-200 flex items-start gap-2 text-emerald-800 text-sm" data-testid="reset-done">
                <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
                <span>Password reset successfully. Redirecting to sign in…</span>
              </div>
            ) : !token ? (
              <div className="mt-6 p-4 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm" data-testid="reset-missing-token">
                <div className="flex items-start gap-2">
                  <MailWarning className="w-5 h-5 shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Reset link missing</div>
                    <div className="mt-1">Please open the link from the email we sent. If it stopped working, request a new one below.</div>
                  </div>
                </div>
                <Link to="/forgot-password" className="mt-3 inline-block text-[#1E3A8A] font-medium hover:underline">Request a new reset link →</Link>
              </div>
            ) : (
              <>
                {tokenError && (
                  <div className="mt-5 p-3 rounded-md border border-red-200 bg-red-50 text-red-800 text-sm flex items-start gap-2" data-testid="reset-token-error">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div>
                      {tokenError}
                      <Link to="/forgot-password" className="ml-1 underline underline-offset-2 font-medium">Request a new link</Link>.
                    </div>
                  </div>
                )}
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
                  <div>
                    <Label>New password</Label>
                    <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" />
                    <div className="text-xs text-slate-500 mt-1">Minimum 6 characters.</div>
                  </div>
                  <div>
                    <Label>Confirm new password</Label>
                    <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" />
                  </div>
                  <Button type="submit" disabled={loading} className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11" data-testid="reset-submit-btn">
                    {loading ? "Saving…" : "Reset Password"}
                  </Button>
                </form>
              </>
            )}

            <div className="mt-5 text-center text-sm text-slate-600">
              <Link to="/login" className="text-[#1E3A8A] font-medium hover:underline">← Back to sign in</Link>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
